/**
 * Your Plan: where a consumer lands after intentionally finishing, and where they come back to. The
 * first arrival after finishing shows "Your plan is ready" with a checkmark and a confetti burst; every
 * later visit (refresh, Back/Forward, sign-in) shows plain "Your plan". The figures stay live, read from
 * the consumer's current data. Back goes to the screen that leads here on this plan's path.
 */
import { CheckCircle } from 'lucide-react';
import { ApplicationsSentSection } from '../components/applications-sent-section';
import { CelebrationConfetti } from '../components/celebration-confetti';
import { EditAccountsLink } from '../components/edit-accounts-link';
import { PolicyReviewLink } from '../components/policy-review-link';
import { SelfFundRenewalCard } from '../components/self-fund-renewal-card';
import { StepFrame } from '../components/step-frame';
import { YourPlanActions } from '../components/your-plan-actions';
import { YourPlanSummaryList } from '../components/your-plan-summary-list';
import { useConfettiBurst } from '../hooks/use-celebration';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { useConsumerPlan } from '../hooks/use-consumer-plan';
import { usePlanReadyArrival } from '../hooks/use-plan-ready-arrival';
import { useYourPlan } from '../hooks/use-your-plan';
import type { ConsumerStepProps } from './step-props';

export function YourPlanStep({ clientId }: ConsumerStepProps) {
  const isFirstArrival = usePlanReadyArrival();
  const isCelebrating = useConfettiBurst(isFirstArrival);
  const { back } = useConsumerFlow(clientId);
  const { model, report, showPlanningSession, isAccessExpired, selfFundAccessEndedAt, previousStep } =
    useYourPlan(clientId);
  const { plan, applications, policyReviews } = useConsumerPlan(clientId);

  return (
    <StepFrame
      step="your-plan"
      title={isFirstArrival ? 'Your plan is ready' : 'Your plan'}
      titleBadge={
        isFirstArrival && (
          <span
            className="bg-primary/10 flex size-9 items-center justify-center rounded-full"
            data-testid="plan-ready-check"
          >
            <CheckCircle className="text-primary size-5" />
          </span>
        )
      }
      subtitle="Here’s how you could pay for long-term care. You can update it any time."
      onBack={() => back(previousStep)}
    >
      {isCelebrating && <CelebrationConfetti />}
      <YourPlanSummaryList
        clientId={clientId}
        summary={model}
        afterRows={
          isAccessExpired ? (
            <SelfFundRenewalCard
              clientId={clientId}
              endsAt={selfFundAccessEndedAt}
            />
          ) : (
            <EditAccountsLink clientId={clientId} />
          )
        }
      />
      <YourPlanActions
        clientId={clientId}
        report={report}
        showPlanningSession={showPlanningSession}
        isAccessExpired={isAccessExpired}
      />
      <ApplicationsSentSection
        clientId={clientId}
        applications={applications}
        selectedQuoteKey={plan?.selectedQuoteKey ?? null}
      />
      <PolicyReviewLink
        clientId={clientId}
        hasPolicyReview={policyReviews.length > 0}
      />
    </StepFrame>
  );
}
